import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { DashboardCard, DashboardCardLayout } from '@catalyst-psa/shared';
import {
  DASHBOARD_CARD_MIN_H,
  DASHBOARD_CARD_MIN_W,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GRID_ROW_HEIGHT,
} from '@catalyst-psa/shared';

/** Gap between cards, in pixels (applied as inner padding so grid maths stay clean). */
const GUTTER = 12;

/**
 * Outer height for a label card with no body text — just enough for the title bar.
 * The stored layout height is untouched; only the rendered box shrinks.
 */
const LABEL_TITLE_ONLY_HEIGHT = 60;

/** A label card whose body is blank renders as a slim title bar. */
function isTitleOnlyLabel(card: DashboardCard): boolean {
  return card.type === 'label' && !(card.config.body ?? '').trim();
}

export interface LayoutEntry extends DashboardCardLayout {
  cardId: string;
}

interface Gesture {
  mode: 'move' | 'resize';
  cardId: string;
  startX: number;
  startY: number;
  origin: DashboardCardLayout;
  columnWidth: number;
}

export interface CardDragHandlers {
  onDragStart: (event: React.PointerEvent<HTMLElement>) => void;
  onResizeStart: (event: React.PointerEvent<HTMLElement>) => void;
}

interface DashboardGridProps {
  cards: DashboardCard[];
  editing: boolean;
  /** Fired once per completed drag/resize with the full layout. */
  onLayoutChange: (layout: LayoutEntry[]) => void;
  renderCard: (card: DashboardCard, handlers: CardDragHandlers) => ReactNode;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * A 12-column dashboard canvas with pointer-driven drag and resize.
 *
 * Hand-rolled rather than pulling in a grid library: the behaviour needed here is small
 * (snap to columns, drag by the header, resize from the corner) and this keeps the
 * bundle and dependency surface unchanged. Placement is free-form — cards stay exactly
 * where they are dropped and are never auto-compacted.
 */
export function DashboardGrid({
  cards,
  editing,
  onLayoutChange,
  renderCard,
}: DashboardGridProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const draftRef = useRef<DashboardCardLayout | null>(null);
  const [draft, setDraft] = useState<{ cardId: string; rect: DashboardCardLayout } | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!active) return undefined;

    const handleMove = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      const dx = (event.clientX - gesture.startX) / gesture.columnWidth;
      const dy = (event.clientY - gesture.startY) / DASHBOARD_GRID_ROW_HEIGHT;
      const origin = gesture.origin;

      const rect =
        gesture.mode === 'move'
          ? {
              w: origin.w,
              h: origin.h,
              x: clamp(Math.round(origin.x + dx), 0, DASHBOARD_GRID_COLUMNS - origin.w),
              y: Math.max(0, Math.round(origin.y + dy)),
            }
          : {
              x: origin.x,
              y: origin.y,
              w: clamp(
                Math.round(origin.w + dx),
                DASHBOARD_CARD_MIN_W,
                DASHBOARD_GRID_COLUMNS - origin.x,
              ),
              h: Math.max(DASHBOARD_CARD_MIN_H, Math.round(origin.h + dy)),
            };

      draftRef.current = rect;
      setDraft({ cardId: gesture.cardId, rect });
    };

    const handleUp = (): void => {
      const gesture = gestureRef.current;
      const rect = draftRef.current;
      gestureRef.current = null;
      draftRef.current = null;
      setActive(false);
      setDraft(null);
      if (!gesture || !rect) return;
      const changed =
        rect.x !== gesture.origin.x ||
        rect.y !== gesture.origin.y ||
        rect.w !== gesture.origin.w ||
        rect.h !== gesture.origin.h;
      if (!changed) return;
      onLayoutChange(
        cards.map((card) => ({
          cardId: card.id,
          ...(card.id === gesture.cardId
            ? rect
            : { x: card.x, y: card.y, w: card.w, h: card.h }),
        })),
      );
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [active, cards, onLayoutChange]);

  const begin = (
    mode: Gesture['mode'],
    card: DashboardCard,
    event: React.PointerEvent<HTMLElement>,
  ): void => {
    if (!editing || event.button !== 0) return;
    event.preventDefault();
    const width = containerRef.current?.clientWidth ?? 0;
    if (width <= 0) return;
    const origin = { x: card.x, y: card.y, w: card.w, h: card.h };
    gestureRef.current = {
      mode,
      cardId: card.id,
      startX: event.clientX,
      startY: event.clientY,
      origin,
      columnWidth: width / DASHBOARD_GRID_COLUMNS,
    };
    draftRef.current = origin;
    setDraft({ cardId: card.id, rect: origin });
    setActive(true);
  };

  const rectFor = (card: DashboardCard): DashboardCardLayout =>
    draft && draft.cardId === card.id ? draft.rect : { x: card.x, y: card.y, w: card.w, h: card.h };

  const bottom = cards.reduce((max, card) => {
    const rect = rectFor(card);
    return Math.max(max, rect.y + rect.h);
  }, 0);

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: Math.max(bottom, 4) * DASHBOARD_GRID_ROW_HEIGHT }}
    >
      {cards.map((card) => {
        const rect = rectFor(card);
        const dragging = draft?.cardId === card.id;
        const heightPx =
          isTitleOnlyLabel(card) && !dragging
            ? Math.min(rect.h * DASHBOARD_GRID_ROW_HEIGHT, LABEL_TITLE_ONLY_HEIGHT)
            : rect.h * DASHBOARD_GRID_ROW_HEIGHT;
        return (
          <div
            key={card.id}
            className={dragging ? 'absolute z-20' : 'absolute z-0'}
            style={{
              left: `${(rect.x / DASHBOARD_GRID_COLUMNS) * 100}%`,
              width: `${(rect.w / DASHBOARD_GRID_COLUMNS) * 100}%`,
              top: rect.y * DASHBOARD_GRID_ROW_HEIGHT,
              height: heightPx,
              padding: GUTTER / 2,
              transition: dragging ? 'none' : 'top 120ms ease, left 120ms ease',
            }}
          >
            <div className={`relative h-full ${dragging ? 'opacity-90 shadow-lg' : ''}`}>
              {renderCard(card, {
                onDragStart: (event) => begin('move', card, event),
                onResizeStart: (event) => begin('resize', card, event),
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
